<template>
  <v-dialog v-model="dialog" width="700" persistent>
    <v-card>
      <v-card-title class="primary white--text">
        {{ $t('esgPlatform.updateStatus') }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-4">
        <!-- Update Status Dropdown -->
        <label class="font-weight-bold d-block mb-1">
          {{ $t('esgPlatform.updateStatus') }}
          <v-icon small color="grey">mdi-help-circle-outline</v-icon>
        </label>
        <v-select
          v-model="selectedStatus"
          :items="statusOptions"
          item-text="label"
          item-value="value"
          outlined
          dense
          class="mb-2"
          :placeholder="$t('esgPlatform.validation.select')"
          :error="statusError"
          :error-messages="statusError ? [$t('esgPlatform.validation.reasonRequired')] : []"

        ></v-select>

        <!-- Comments — always shown for both approve and reject -->
        <label class="font-weight-bold d-block mb-1">{{ $t('esgPlatform.validation.comments') }}</label>
        <v-textarea
          v-model="comment"
          :label="$t('esgPlatform.validationRejectionReasonPlaceholder')"
          outlined
          rows="4"
          class="mb-4"
          :error="commentError"
          :error-messages="commentError ? [$t('esgPlatform.validation.reasonRequired')] : []"
        ></v-textarea>

        <v-divider class="mb-4"></v-divider>

        <!-- Current Status — driven by latest updateHistory entry -->
        <div class="mb-1 font-weight-bold">{{ $t('esgPlatform.validation.currentStatus') }}:</div>
        <template v-if="currentEntry">
          <div class="d-flex align-center mb-2" style="gap:16px;">
            <!-- Left: icon + status -->
            <div class="d-flex align-center" style="gap:8px; min-width:110px;">
              <v-icon :color="currentEntry.approveReason ? '#10b981' : '#ef4444'" size="28">
                {{ currentEntry.approveReason ? 'mdi-check-decagram' : 'mdi-cancel' }}
              </v-icon>
              <span class="font-weight-bold" :style="{ color: currentEntry.approveReason ? '#10b981' : '#ef4444' }">
                {{ currentEntry.approveReason ? $t('esgPlatform.approved') : $t('esgPlatform.rejected') }}
              </span>
            </div>
            <!-- Right: Updated By label + name + date stacked -->
            <div>
              <div class="font-weight-bold" style="font-size:13px; color:#111827;">{{ $t('esgPlatform.validation.updatedBy') }}:</div>
              <div class="caption grey--text text--darken-1">
                {{ currentEntry.updatedByUserName || '—' }}&nbsp;&nbsp;{{ formatDate(currentEntry.createdAt) }}
              </div>
            </div>
          </div>
          <div class="status-comments my-3">
            <div class="status-comments-title">{{ $t('esgPlatform.validation.comments') }}:</div>
            <div class="status-comments-text">{{ currentEntry.approveReason || currentEntry.rejectionReason }}</div>
          </div>
        </template>
        <template v-else-if="item && item.approveReason">
          <!-- Fallback if no updateHistory -->
          <div class="d-flex align-center mb-1" style="gap:8px;">
            <v-icon color="#10b981" size="28">mdi-check-decagram</v-icon>
            <span class="font-weight-bold" style="color:#10b981;">{{ $t('esgPlatform.approved') }}</span>
          </div>
          <div class="status-comments mt-1">
            <div class="status-comments-title">{{ $t('esgPlatform.validation.comments') }}:</div>
            <div class="status-comments-text">{{ item.approveReason }}</div>
          </div>
        </template>
        <template v-else-if="item && item.rejectionReason">
          <div class="d-flex align-center mb-1" style="gap:8px;">
            <v-icon color="#ef4444" size="28">mdi-cancel</v-icon>
            <span class="font-weight-bold" style="color:#ef4444;">{{ $t('esgPlatform.rejected') }}</span>
          </div>
          <div class="status-comments mt-1">
            <div class="status-comments-title">{{ $t('esgPlatform.validation.comments') }}:</div>
            <div class="status-comments-text">{{ item.rejectionReason }}</div>
          </div>
        </template>
        <template v-else>
          <div class="d-flex align-center mb-1" style="gap:8px;">
            <v-icon color="#9ca3af" size="28">mdi-progress-clock</v-icon>
            <span class="font-weight-bold" style="color:#9ca3af;">{{ $t('esgPlatform.pending') }}</span>
          </div>
        </template>

        <v-divider class="mb-3"></v-divider>

        <div class="font-weight-bold mb-2">{{ $t('esgPlatform.validation.statusUpdateHistory') }}:</div>
        <template v-if="historyEntries.length">
          <div
            v-for="(entry, idx) in historyEntries"
            :key="idx"
            class="d-flex align-start mb-3"
            style="gap:10px;"
          >
            <div class="d-flex align-center" style="gap:16px;">
              <div class="d-flex align-center" style="gap:8px; min-width:110px;">
                <v-icon
                  :color="entry.approveReason ? '#10b981' : '#ef4444'"
                  size="22"
                >
                  {{ entry.approveReason ? 'mdi-check-decagram' : 'mdi-cancel' }}
                </v-icon>
                <span class="font-weight-bold" :style="{ color: entry.approveReason ? '#10b981' : '#ef4444' }">
                  {{ entry.approveReason ? $t('esgPlatform.approved') : $t('esgPlatform.rejected') }}
                </span>
              </div>

              <div>
                <div class="font-weight-bold" style="font-size:13px; color:#111827;">{{ $t('esgPlatform.validation.updatedBy') }}:</div>
                <div class="caption grey--text text--darken-1">
                  {{ entry.updatedByUserName || '—' }}&nbsp;&nbsp;{{ formatDate(entry.createdAt) }}
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="caption grey--text mb-2">{{ $t('esgPlatform.validation.noHistoryYet') }}</div>

      </v-card-text>
      <v-card-actions class="justify-end pb-4 pr-4">
        <v-btn color="primary" outlined @click="cancel">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" @click="confirm">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  computed: {
    currentEntry() {
      return this.item && this.item.updateHistory && this.item.updateHistory.length
        ? this.item.updateHistory[0]
        : null;
    },
    historyEntries() {
      return this.item && this.item.updateHistory && this.item.updateHistory.length > 1
        ? this.item.updateHistory.slice(1)
        : [];
    },
    statusOptions() {
      return [
        { label: this.$t('esgPlatform.validation.approveAction'), value: 'approve' },
        { label: this.$t('esgPlatform.validation.rejectAction'), value: 'reject' },
      ];
    },
  },
  data() {
    return {
      selectedStatus: '',
      comment: '',
      commentError: false,
      statusError:false
    };
  },
  watch: {
    dialog(newVal) {
      if (!newVal) {
        // Reset on close
        this.selectedStatus = '';
        this.comment = '';
      }
    },
     comment(val) {
    if (val.trim()) {
      this.commentError = false;
      this.statusError = false;
    }
  }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const locale = this.$i18n?.locale || 'en';
      const day = String(d.getDate()).padStart(2, '0');
      const month = d.toLocaleString(locale, { month: 'short' }).replace('.', '').toUpperCase();
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    },
    cancel() {
      this.commentError = false;
      this.statusError = false;
      this.$emit('cancel');
    },
    confirm() {
        if (this.selectedStatus === 'reject' && !this.comment.trim()) {
          this.commentError = true
        return;
        }
      if (!this.selectedStatus) {
        this.statusError = !this.selectedStatus;
        return;
      }
      this.commentError = false;
      this.statusError = false;

  const approveComment = this.comment.trim() || '-';
  this.$emit('confirm', {
    approveReason: this.selectedStatus === 'approve' ? approveComment : null,
    rejectionReason: this.selectedStatus === 'reject' ? this.comment : null,
  });
    },
  },
};
</script>

<style scoped>
.status-comments {
  margin-top: 8px;
}
.status-comments-title {
  font-size: 12px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2px;
}
.status-comments-text {
  font-size: 13px;
  color: #374151;
  word-break: break-word;
  white-space: normal;
  line-height: 1.5;
}


</style>
